import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../lib/auth';
import { useSuccessAlert, useErrorAlert } from './AlertSystem';
import './AuthPage.css';

interface FormErrors {
  email?: string;
  general?: string;
}

const ForgotPasswordPage: React.FC = () => {
  const showSuccess = useSuccessAlert();
  const showError = useErrorAlert();
  
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Email validation
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setEmail(value);

    // Clear error when user starts typing
    if (errors.email) {
      setErrors(prev => ({ ...prev, email: undefined }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      await authService.forgotPassword(email);
      showSuccess('Password reset email sent! Please check your inbox.');
      setEmail(''); // Clear the form
      
    } catch (error) {
      console.error('Forgot password error:', error);
      showError(error instanceof Error ? error.message : 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-background" />
      
      <div className="container">
        <div className="auth-container">
          
          {/* Auth Header */}
          <div className="auth-header">
            <div className="auth-logo">
              <div className="auth-logo-icon">
                <i className="ri-shield-keyhole-line"></i>
              </div>
              <span className="auth-logo-text">Nuroveli</span>
            </div>
            
            <h1 className="auth-title">Forgot Password</h1>
            <p className="auth-subtitle">
              Enter your email address and we'll send you a link to reset your password
            </p>
          </div>

          {/* Forgot Password Form */}
          <form className="auth-form" onSubmit={handleSubmit}>
            
            {/* General Error */}
            {errors.general && (
              <div className="auth-error">
                <i className="ri-error-warning-line"></i>
                <span>{errors.general}</span>
              </div>
            )}

            {/* Email Field */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="email">
                Email Address
              </label>
              <div className="auth-input-wrapper">
                <div className="auth-input-icon">
                  <i className="ri-mail-line"></i>
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={`auth-input ${errors.email ? 'auth-input-error' : ''}`}
                  placeholder="Enter your email"
                  value={email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  autoFocus
                />
              </div>
              {errors.email && (
                <span className="auth-field-error">{errors.email}</span>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="auth-btn auth-btn-primary"
              disabled={isLoading}
            >
              <span className="auth-btn-text">
                {isLoading ? 'Sending Reset Link...' : 'Send Reset Link'}
              </span>
              <div className="auth-btn-icon">
                <i className={isLoading ? 'ri-loader-4-line auth-spinner' : 'ri-mail-send-line'}></i>
              </div>
              <div className="auth-btn-glow"></div>
            </button>

            {/* Back to Login */}
            <div className="auth-form-footer">
              <Link to="/login" className="auth-link">
                <i className="ri-arrow-left-line"></i>
                Back to Login
              </Link>
            </div>

          </form>

          {/* Auth Footer */}
          <div className="auth-footer">
            <p className="auth-footer-text">
              Don't have an account? 
              <Link to="/register" className="auth-footer-link">
                Create one here
              </Link>
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
